"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Shield,
  Sparkles,
  ShieldCheck,
  Lock,
  Eye,
  Radio,
  Crosshair,
  Activity,
  Building2,
  Crown,
  Vote,
  ArrowRight,
  Mic,
  Compass,
  Server,
  Zap,
  Target,
  AlertTriangle,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { TreeBlock } from "@/lib/blocks/tree-block";
import { CounterBlock } from "@/lib/blocks/counter-block";
import { FeatureGridBlock } from "@/lib/blocks/feature-grid-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const HEADLINE_STATS = [
  { metric: "0", suffix: "", label: "Security Breaches", sub: "Fifteen-plus years", accent: "emerald" },
  { metric: "99.9999", suffix: "%", label: "Uptime", sub: "31.5 sec max/year", accent: "cyan" },
  { metric: "89", suffix: "%", label: "Prediction Accuracy", sub: "Crisis forecasting", accent: "purple" },
  { metric: "72", suffix: "h", label: "Advance Warning", sub: "Over conventional intel", accent: "amber-gold" },
  { metric: "500", suffix: "M+", label: "Data Points Daily", sub: "Real-time processing", accent: "red" },
  { metric: "60", suffix: "min", label: "Intel-to-Decision", sub: "Reduced from 24-72 hours", accent: "denim" },
];

const SECURITY_LAYERS = [
  { num: "01", title: "Quantum-Resistant Encryption", body: "CRYSTALS-Kyber-768 for key encapsulation and CRYSTALS-Dilithium3 for digital signatures — both NIST-standardized post-quantum algorithms. Hybrid encryption combining classical and post-quantum key exchange, ensuring communications intercepted today remain secure even when quantum computers become operational.", accent: "emerald" },
  { num: "02", title: "Zero-Trust Architecture", body: "Seven independent security layers with no single point of failure. Network segmentation, application isolation, identity-aware access controls, continuous behavioral monitoring, and automated threat response. Compromise at any layer is contained before propagation.", accent: "cyan" },
  { num: "03", title: "Air-Gap Deployment", body: "Complete operational functionality maintained without internet connectivity for classified environments. Local threat detection and signature updates from air-gapped stations with cryptographic verification. Suitable for SCIFs and classified operational facilities where internet connectivity is neither available nor permitted.", accent: "purple" },
  { num: "04", title: "Sovereign Infrastructure", body: "Zero third-party dependencies across the entire technology stack. No licensed components, no third-party services, no supply chain vulnerabilities. The infrastructure was built over fifteen-plus years on data centers and mainframes that cannot be replicated without the same decade of investment.", accent: "amber-gold" },
  { num: "05", title: "Hardware Security Modules", body: "FIPS 140-3 Level 3 certified with tamper switches, zeroization circuits, and epoxied components. Cryptographic keys never leave dedicated hardware. Master Key, KEK, DEK hierarchy with m-of-n quorum. Key rotation customer-defined. Key revocation instantaneous.", accent: "red" },
  { num: "06", title: "Compliance & Governance", body: "ISO 27001, SOC 2 Type II, FedRAMP High-ready, IL-4/IL-6 compliance. Automated compliance monitoring across GDPR, HIPAA, PCI DSS, and fifty-plus pre-built compliance frameworks. Immutable audit trails. Policy-as-Code with adaptive enforcement.", accent: "denim" },
];

const CHALLENGES = [
  { icon: Lock, title: "Secure Communications", body: "Every unencrypted communication is an intelligence asset for the adversary. Every intercepted metadata point reveals patterns, relationships, and operational intent. The standard is not encryption — it is sovereign-grade encryption with complete metadata elimination, quantum-resistant algorithms, and zero third-party dependencies.", accent: "emerald" },
  { icon: Eye, title: "Real-Time Threat Detection", body: "Threats emerge from the convergence of signals across social platforms, dark web forums, encrypted channels, financial transactions, and ground-level intelligence. Real-time threat detection monitors two hundred-plus digital platforms, one hundred thousand news sources, and one thousand-plus dark web sources simultaneously.", accent: "cyan" },
  { icon: Radio, title: "Intelligence Fusion", body: "Intelligence fragmented across agencies is intelligence that fails. When signals intelligence, human intelligence, open-source intelligence, and geospatial intelligence reside in incompatible systems, decision-makers receive fragments rather than pictures. Multi-agency fusion reduces coordination from days to minutes.", accent: "purple" },
  { icon: Crosshair, title: "Counter-Intelligence", body: "The adversary is not only external. Insider threats, compromised supply chains, and sophisticated state-sponsored espionage campaigns target defense infrastructure from within. Counter-intelligence and cyber forensics identify threats that conventional security tools miss.", accent: "amber-gold" },
  { icon: ShieldCheck, title: "Border Security & Counter-Terrorism", body: "Border regions represent the convergence of multiple threat vectors — smuggling networks, cross-border terrorism, migration crises, and organized crime. Ground-truth validation combines booth-level field intelligence with digital signals analysis to deliver operational pictures that reflect ground reality.", accent: "red" },
  { icon: Server, title: "Critical Infrastructure Protection", body: "Energy grids, communications networks, transportation systems, water treatment facilities, and financial infrastructure. When targeted — through cyber attack, physical sabotage, or insider compromise — consequences cascade across every dimension of national stability.", accent: "denim" },
];

const INTELLIGENCE_DIMENSIONS = [
  { title: "Dynamic Intelligence", body: "Real-time, sub-sixty-second delivery. The intelligence that enables immediate response when seconds separate containment from catastrophe. Dark web alerting for threat actor chatter and data leaks. Critical alerts reach decision-makers within sixty seconds.", accent: "red" },
  { title: "Operational Intelligence", body: "Current, continuously updated. The intelligence that guides day-to-day operations with information never more than minutes old. Threat status monitoring with automated escalation. Operational tempo metrics measuring intelligence-to-decision cycles.", accent: "cyan" },
  { title: "Tactical Intelligence", body: "Mid-term, one-to-four-week horizon. The intelligence that refines ongoing operations. Message resonance analysis with probability-weighted outcome projections. Competitive response playbooks pre-built for likely adversary moves.", accent: "purple" },
  { title: "Strategic Intelligence", body: "Long-term, one-to-five-year horizon. The intelligence that informs multi-year decisions — alliance strategy, force posture transformation, geopolitical positioning. Multi-year trend analysis with probability-weighted outcomes stress-tested against historical analogues.", accent: "amber-gold" },
  { title: "Predictive Intelligence", body: "Forecasting, event horizon to twelve months. Twenty-four to seventy-two-hour crisis early warning. Threat escalation prediction distinguishing which emerging threats will escalate. Eighty-nine percent accuracy in viral trajectory prediction through ensemble models trained on billions of historical propagation patterns.", accent: "emerald" },
];

const CLIENTS = [
  { title: "Military Organizations", body: "Defense ministries, armed forces commands, and military intelligence agencies requiring strategic threat assessment, military operational intelligence, defense industrial base protection, and multi-source intelligence fusion across classification boundaries.", accent: "red" },
  { title: "Paramilitary Forces", body: "Internal security forces, border guard organizations, and rapid deployment units requiring real-time tactical intelligence, border security intelligence, ground-truth validation, and secure field communications.", accent: "emerald" },
  { title: "Law Enforcement Agencies", body: "Federal, state, and local law enforcement requiring crime pattern analysis, predictive policing intelligence, organized crime network mapping, cyber-enabled crime intelligence, and cross-jurisdictional intelligence fusion.", accent: "cyan" },
  { title: "National Security Agencies", body: "Intelligence services, security directorates, and national security councils requiring comprehensive intelligence architecture spanning geopolitical intelligence, counter-intelligence, cyber threat intelligence, and strategic early warning systems.", accent: "purple" },
  { title: "Allied Intelligence Partnerships", body: "Multilateral intelligence-sharing arrangements requiring secure cross-border intelligence fusion, interoperable security architectures, and classification-level bridging across allied nations with different security standards.", accent: "amber-gold" },
  { title: "Defense Industrial Base", body: "Defense contractors and weapons systems manufacturers requiring supply chain security, intellectual property protection, executive threat assessment, and competitive intelligence against state-sponsored industrial espionage campaigns.", accent: "denim" },
];

export default function DefenseIntelligencePage() {
  return (
    <>
      <PageHero
        icon={Shield}
        overline="Defense & Intelligence"
        overlineClassName="text-emerald"
        title={
          <>
            Sovereign-Grade Security. <span className="text-gradient-premium">Intelligence at the Speed of Operations.</span>
          </>
        }
        subtitle="The difference between a nation that anticipates threats and one that merely responds to them is measured in the speed, depth, and accuracy of its intelligence architecture. Dr. Jyoti Kush builds and operates the systems that close this gap."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Sectors", href: "/sectors/" }, { label: "Defense & Intelligence" }]}
        badge="Zero breaches. Fifteen-plus years."
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-5`}>
              <Sparkles className="size-3.5 text-cyan" />
              <span className="text-overline text-cyan">Executive Summary</span>
            </div>
            <h2 className="text-display-md mb-5">
              Intelligence asymmetry that determines national outcomes.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              Military, paramilitary, law enforcement, and national security agencies across eighteen countries on three continents have engaged this capability for engagements where failure is measured in national stability. The record stands: zero security breaches across fifteen-plus years, ninety-nine point nine nine nine nine percent platform uptime, and an operational methodology that processes over five hundred million data points daily to deliver intelligence that decision-makers can act upon within minutes, not days.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The defense and intelligence capabilities draw from two foundational pillars of the Penta P Framework: Policing, Intelligence & Defense — which provides the intelligence architecture, threat detection, and operational coordination — and Privacy, Encryption & Information Security — which provides the sovereign security substrate that protects every communication, every data store, and every intelligence product from interception, exfiltration, or compromise.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              These are not point solutions. They are integrated systems — built over fifteen-plus years, refined through hundreds of engagements across the world&apos;s most demanding security environments, and proven by a record that no competitor can match. The discipline forged in clinical practice — diagnose before treating, triage under pressure, follow protocol while preserving judgment, own the outcome, do no harm — is the same discipline that governs every defense engagement.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {HEADLINE_STATS.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${ACCENT[s.accent].bg} to-transparent border ${ACCENT[s.accent].border} hover:scale-[1.01] transition-all`}
              >
                <div className={`text-display-sm font-bold ${ACCENT[s.accent].text} leading-none mb-2`}>
                  {s.metric}{s.suffix}
                </div>
                <div className="text-caption font-bold uppercase tracking-wider mb-1">{s.label}</div>
                <div className="text-caption text-muted-foreground">{s.sub}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <AlertTriangle className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">The Defense Challenge</span>
          </div>
          <h2 className="text-display-md mb-4">Six operational requirements at defense tempo.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Defense and intelligence agencies operate where the adversaries are nation-states, the stakes are existential, and the tempo is measured in seconds. The challenges are operational, immediate, and unforgiving.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {CHALLENGES.map((c, i) => {
            const a = ACCENT[c.accent];
            return (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl" />
                <div className="relative">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center mb-4 shadow-lg`}>
                    <c.icon className="size-6 text-white" />
                  </div>
                  <h3 className={`text-heading-md mb-3 ${a.text}`}>{c.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{c.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Lock className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Security Standard</span>
          </div>
          <h2 className="text-display-md mb-4">Quantum-resistant, zero-trust, air-gap ready.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Defense agencies do not select security on features. They select it on survival. The security architecture meets the standard that the world&apos;s most demanding adversaries have failed to breach — not because they lacked resources, but because the architecture was designed to withstand them.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {SECURITY_LAYERS.map((s, i) => {
            const a = ACCENT[s.accent];
            return (
              <motion.div
                key={s.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl" />
                <div className="relative">
                  <div className={`text-display-sm font-bold ${a.text} opacity-30 leading-none mb-3`}>{s.num}</div>
                  <h3 className={`text-heading-md mb-3 ${a.text}`}>{s.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{s.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Zap className="size-3.5 text-purple" />
            <span className="text-overline text-purple">The Five-Dimensional Intelligence Framework</span>
          </div>
          <h2 className="text-display-md mb-4">Intelligence across every tempo of defense decisions.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Defense decisions are not made at a single tempo. Some require sub-sixty-second response. Others require multi-year strategic forecasting. The framework operates across five temporal dimensions, ensuring decision-makers receive the right intelligence at the right tempo.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-3">
          {INTELLIGENCE_DIMENSIONS.map((d, i) => {
            const a = ACCENT[d.accent];
            return (
              <motion.div
                key={d.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`relative glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <div className="text-display-sm font-bold text-foreground/30 leading-none mb-3">{`0${i + 1}`}</div>
                <h3 className={`text-heading-md mb-2 ${a.text}`}>{d.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{d.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Target className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">The Intelligence Gap</span>
          </div>
          <h2 className="text-display-md mb-4">Three critical gaps conventional security cannot close.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The distinction between conventional cybersecurity and sovereign-grade security is not incremental. It is categorical. Three gaps manifest in dimensions that defense agencies cannot afford.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { num: "01", title: "The Detection Gap", body: "The industry average for breach detection is two hundred eighty-seven days — nine months during which an adversary has access. Mean time to detect reduced from two hundred twelve days to fourteen hours — a ninety-three point five percent improvement.", accent: "red" },
            { num: "02", title: "The Coordination Gap", body: "Intelligence fragmented across agencies creates decision latency measured in days. Multi-agency intelligence fusion reduced from twenty-four to seventy-two hours to under five minutes — eliminating coordination failure that allows threats to exploit spaces between agencies.", accent: "cyan" },
            { num: "03", title: "The Prediction Gap", body: "Agencies that lack predictive intelligence respond to events after they occur. Eighty-nine percent prediction accuracy with seventy-two-hour average advance warning — transforming defense from reactive to preemptive.", accent: "purple" },
          ].map((g, i) => {
            const a = ACCENT[g.accent];
            return (
              <motion.div
                key={g.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl" />
                <div className="relative">
                  <div className={`text-display-sm font-bold ${a.text} opacity-30 leading-none mb-3`}>{g.num}</div>
                  <h3 className={`text-heading-md mb-3 ${a.text}`}>{g.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{g.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Activity className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Ideal Clients</span>
          </div>
          <h2 className="text-display-md mb-4">Who benefits from this engagement.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Organizations for whom security is not a preference but a survival requirement. Intelligence is not a convenience but a prerequisite for operational success. Compromise is not a risk but an existential threat.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {CLIENTS.map((c, i) => {
            const a = ACCENT[c.accent];
            return (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <h3 className={`text-heading-md mb-2 ${a.text}`}>{c.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{c.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <span className="text-overline text-emerald">Signature Architecture</span>
          </div>
          <h2 className="text-display-md mb-4">Defense Capability Tree.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Five capability roots extend into the operational fabric that protects national security. Each branch delivers a sovereign-grade capability that no conventional security provider can replicate.
          </p>
        </div>
        <TreeBlock
          eyebrow="Capability Architecture"
          title="Five Roots. Fifteen Branches. Sovereign-Grade Defense."
          description="Expand each root to explore the operational branches that compose the integrated defense and intelligence capability."
          entries={[
            {
              id: "root-1",
              label: "Quantum-Resilient Cryptography",
              body: "Post-quantum algorithms protecting communications against current and future quantum computing threats.",
              icon: <Lock className="size-4" />,
              accent: "emerald",
              children: [
                { id: "root-1-a", label: "CRYSTALS-Kyber-768 Key Encapsulation", body: "NIST-standardized post-quantum key exchange providing forward secrecy against harvest-now-decrypt-later attacks." },
                { id: "root-1-b", label: "CRYSTALS-Dilithium3 Signatures", body: "Post-quantum digital signatures validated by NIST for sovereign communication authentication." },
                { id: "root-1-c", label: "Hybrid Classical-Quantum Exchange", body: "AES-256-GCM at rest, ChaCha20-Poly1305 in transit, RSA-4096 and X25519 asymmetric, all combined with post-quantum layers." },
              ],
            },
            {
              id: "root-2",
              label: "Zero-Trust Defense-in-Depth",
              body: "Seven independent security layers with no single point of failure across the deployment surface.",
              icon: <ShieldCheck className="size-4" />,
              accent: "cyan",
              children: [
                { id: "root-2-a", label: "Network Segmentation & Application Isolation", body: "Micro-segmentation, container boundaries, and identity-aware access controls evaluated at request time." },
                { id: "root-2-b", label: "Continuous Behavioral Monitoring", body: "UEBA-driven anomaly detection with automated playbook containment triggered within seconds of detection." },
                { id: "root-2-c", label: "Air-Gapped Recovery Systems", body: "Full operational functionality without internet connectivity for SCIFs and classified environments." },
              ],
            },
            {
              id: "root-3",
              label: "Five-Dimensional Intelligence",
              body: "Intelligence operating across every tempo at which defense decisions are made.",
              icon: <Eye className="size-4" />,
              accent: "purple",
              children: [
                { id: "root-3-a", label: "Dynamic & Operational Intelligence", body: "Sub-60-second alerting across 200+ platforms, continuously updated operational dashboards with minutes-old information." },
                { id: "root-3-b", label: "Tactical & Strategic Intelligence", body: "1-4 week tactical analysis and 1-5 year strategic forecasting with probability-weighted outcomes." },
                { id: "root-3-c", label: "Predictive Intelligence", body: "89% accuracy with 72-hour crisis early warning, threat escalation prediction, and viral trajectory forecasting." },
              ],
            },
            {
              id: "root-4",
              label: "Sovereign Infrastructure",
              body: "Zero third-party dependencies across the entire technology stack — hardware, network, and application layers.",
              icon: <Server className="size-4" />,
              accent: "amber-gold",
              children: [
                { id: "root-4-a", label: "Hardware Security Modules", body: "FIPS 140-3 Level 3 certified with tamper switches, zeroization circuits, and epoxied components. Master Key, KEK, DEK hierarchy with m-of-n quorum." },
                { id: "root-4-b", label: "National Key Escrow & Source Code Escrow", body: "Government retains access independent of any vendor. Complete data residency compliance across all deployment models." },
              ],
            },
            {
              id: "root-5",
              label: "Counter-Intelligence & Forensics",
              body: "Detection, neutralization, and exploitation of hostile intelligence activities targeting defense infrastructure.",
              icon: <Crosshair className="size-4" />,
              accent: "red",
              children: [
                { id: "root-5-a", label: "Insider Threat & Behavioral Analytics", body: "Pattern-of-life analysis detecting compromised personnel before critical data exposure occurs." },
                { id: "root-5-b", label: "Cyber Forensics Chain of Custody", body: "ISO 27037 compliant evidence preservation, attack timeline reconstruction, threat actor attribution, and post-incident hardening." },
                { id: "root-5-c", label: "Counter-Intelligence Operations", body: "Foreign intelligence service detection, corporate espionage identification, and information operation mapping through narrative analysis." },
              ],
            },
          ]}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <span className="text-overline text-cyan">Verified Performance Data</span>
          </div>
          <h2 className="text-display-md mb-4">Operational metrics across sovereign deployments.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The numbers below are not marketing claims. They are verified operational outcomes drawn from fifteen-plus years of engagements where the margin for error is measured in national stability.
          </p>
        </div>
        <CounterBlock
          columns={3}
          stats={[
            { value: 0, suffix: "", label: "Security Breaches", description: "Across all engagements, all platforms, 15+ years", accent: "emerald" },
            { value: 14, suffix: "h", label: "Mean Time to Detect", description: "Reduced from 212 days — 93.5% improvement", accent: "cyan" },
            { value: 9, suffix: "h", label: "Mean Time to Respond", description: "Reduced from 75 days — 88% improvement", accent: "purple" },
            { value: 95, suffix: "%", label: "Coordination Success", description: "LITHVIK N1 across all concurrent operations", accent: "amber-gold" },
            { value: 347, suffix: "%", label: "Average ROI", description: "Three-year deployment return on investment", accent: "red" },
            { value: 18, suffix: "", label: "Countries Served", description: "Three continents of continuous operations", accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <span className="text-overline text-amber-gold">Specialized Defense Services</span>
          </div>
          <h2 className="text-display-md mb-4">Twelve integrated services across the defense spectrum.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The defense portfolio extends beyond intelligence fusion into the full range of capabilities that sovereign security demands. Each service is coordinated through the integrated platform architecture.
          </p>
        </div>
        <FeatureGridBlock
          columns={3}
          features={[
            { title: "Open Source Intelligence", body: "Systematic collection across 200+ platforms, 100K+ news sources, 1,000+ dark web sources in 50+ languages.", icon: <Radio className="size-5" />, accent: "emerald" },
            { title: "Cyber Threat Intelligence", body: "Threat actor profiling, vulnerability intelligence, dark web monitoring with real-time IOC collection.", icon: <ShieldCheck className="size-5" />, accent: "cyan" },
            { title: "Geopolitical Forecasting", body: "International relations monitoring, sanctions tracking, and conflict early warning across 195+ countries.", icon: <Compass className="size-5" />, accent: "purple" },
            { title: "Border Security Intelligence", body: "Cross-border threat tracking, smuggling network intelligence, geospatial satellite-to-ground validation.", icon: <Crosshair className="size-5" />, accent: "amber-gold" },
            { title: "Critical Infrastructure Protection", body: "Vulnerability identification across physical, cyber, personnel, and supply chain dimensions for energy and financial systems.", icon: <Server className="size-5" />, accent: "red" },
            { title: "Tactical Intelligence", body: "Pre-event intelligence packs, adversary positioning briefs, and sub-60-second alerts for defined tactical events.", icon: <Zap className="size-5" />, accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-emerald/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-cyan/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Sparkles className="size-10 text-emerald mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Engage Dr. Jyoti Kush.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Defense and intelligence engagements begin with the same discipline that has produced zero security incidents across fifteen-plus years. National security level confidentiality from first contact. The standard is absolute. The architecture is sovereign.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Begin an Engagement
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/sectors/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Compass className="size-4" />
                All Sectors
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Governments", description: "Governance transformation at sovereign scale.", href: "/sectors/governments/", icon: Building2, accent: "red" },
          { title: "Presidential & PM Offices", description: "Strategic command at the highest level.", href: "/sectors/presidential-pm-offices/", icon: Crown, accent: "amber-gold" },
          { title: "Political Organizations", description: "Intelligence that powers electoral outcomes.", href: "/sectors/political-organizations/", icon: Vote, accent: "cyan" },
          { title: "Sectors Hub", description: "Eight sectors. One operating system.", href: "/sectors/", icon: Building2, accent: "emerald" },
        ]}
      />
    </>
  );
}
